import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  Plus,
  Trash2,
  Save,
  GripVertical,
  ChevronDown,
  ChevronUp,
  FileText,
  Loader
} from 'lucide-react';
import { doc, setDoc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { DndContext, DragEndEvent, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface Rule {
  id: string;
  text: string;
  order: number;
  createdAt: Timestamp;
  expanded?: boolean;
}

interface MarcasRulesModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  marcas: Array<{ id: string; name: string }>;
}

// Componente para una regla individual con drag & drop
const SortableRule: React.FC<{
  rule: Rule;
  onUpdate: (text: string) => void;
  onDelete: () => void;
  onToggleExpand: () => void;
  marcas: Array<{ id: string; name: string }>;
}> = ({ rule, onUpdate, onDelete, onToggleExpand, marcas }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(rule.text);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: rule.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Renderizar texto con @menciones como pills
  const renderTextWithMentions = (text: string) => {
    // Crear un mapa de marcas para búsqueda rápida
    const marcaMap = new Map(marcas.map(m => [m.name.toLowerCase(), m.name]));
    
    // Regex mejorado que captura @palabra y opcionalmente más palabras después
    // Captura desde @ hasta que encuentra: otro @, fin de línea, o puntuación seguida de espacio
    const parts: Array<{ type: 'text' | 'mention'; content: string }> = [];
    let lastIndex = 0;
    
    // Buscar todas las posibles menciones que empiezan con @
    const mentionRegex = /@([^\s@][^@,.\n]*)/g;
    let match;
    
    while ((match = mentionRegex.exec(text)) !== null) {
      // Agregar el texto antes de la mención
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: text.substring(lastIndex, match.index)
        });
      }
      
      // El texto capturado después del @
      let capturedText = match[1].trim();
      
      // Buscar la marca más larga que coincida desde el inicio
      let foundMarca: string | null = null;
      
      // Intentar encontrar coincidencias exactas primero
      if (marcaMap.has(capturedText.toLowerCase())) {
        foundMarca = marcaMap.get(capturedText.toLowerCase())!;
      } else {
        // Si no hay coincidencia exacta, buscar coincidencias parciales
        // desde la más larga hasta la más corta
        const sortedMarcas = [...marcas].sort((a, b) => b.name.length - a.name.length);
        
        for (const marca of sortedMarcas) {
          if (capturedText.toLowerCase().startsWith(marca.name.toLowerCase())) {
            foundMarca = marca.name;
            break;
          }
        }
      }
      
      if (foundMarca) {
        parts.push({
          type: 'mention',
          content: foundMarca
        });
        // Actualizar el índice para continuar después de la marca encontrada
        lastIndex = match.index + 1 + foundMarca.length; // +1 por el @
        
        // Si hay texto adicional después de la marca, agregarlo
        const remainingInMatch = capturedText.substring(foundMarca.length);
        if (remainingInMatch) {
          parts.push({
            type: 'text',
            content: remainingInMatch
          });
          lastIndex = match.index + match[0].length;
        }
      } else {
        // No es una marca válida, mostrar como texto normal
        parts.push({
          type: 'text',
          content: match[0]
        });
        lastIndex = match.index + match[0].length;
      }
    }
    
    // Agregar cualquier texto restante
    if (lastIndex < text.length) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex)
      });
    }
    
    // Renderizar las partes
    return parts.map((part, index) => {
      if (part.type === 'mention') {
        return (
          <span
            key={`mention-${index}`}
            className="inline-flex items-center px-2 py-0.5 mx-1 text-sm font-medium"
            style={{
              backgroundColor: 'rgba(219, 39, 119, 0.1)',
              color: 'rgb(219, 39, 119)',
              borderRadius: '9999px',
              border: '1px solid rgba(219, 39, 119, 0.3)'
            }}
          >
            @{part.content}
          </span>
        );
      }
      return <span key={`text-${index}`}>{part.content}</span>;
    });
  };

  const handleSave = () => {
    onUpdate(editText);
    setIsEditing(false);
  };

  const isExpanded = rule.expanded !== false && (isEditing || rule.text.length <= 200);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border border-gray-200 rounded-lg bg-white ${isDragging ? 'shadow-lg' : ''}`}
    >
      <div className="flex items-start p-3">
        <div
          {...attributes}
          {...listeners}
          className="cursor-move mt-1 mr-2 text-gray-400 hover:text-gray-600"
        >
          <GripVertical className="w-4 h-4" />
        </div>

        <div className="flex-1">
          {isEditing ? (
            <div>
              <textarea
                ref={textareaRef}
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 resize-none"
                style={{
                  borderColor: 'rgba(219, 39, 119, 0.3)',
                  '--tw-ring-color': 'rgb(219, 39, 119)'
                } as React.CSSProperties}
                rows={4}
                placeholder="Escribe una regla... Usa @NombreMarca para referenciar marcas"
                autoFocus
              />
              <div className="flex justify-end gap-2 mt-2">
                <button
                  onClick={() => {
                    setEditText(rule.text);
                    setIsEditing(false);
                  }}
                  className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  className="px-3 py-1 text-sm text-white rounded hover:opacity-90"
                  style={{ backgroundColor: 'rgb(219, 39, 119)' }}
                >
                  Guardar
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div 
                className={`text-gray-700 ${!isExpanded ? 'line-clamp-2' : ''} cursor-pointer`}
                onClick={() => setIsEditing(true)}
              >
                {renderTextWithMentions(rule.text)}
              </div>
              {rule.text.length > 200 && (
                <button
                  onClick={onToggleExpand}
                  className="text-xs mt-1 flex items-center gap-1"
                  style={{ color: 'rgb(219, 39, 119)' }}
                >
                  {isExpanded ? (
                    <>
                      <ChevronUp className="w-3 h-3" />
                      Ver menos
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-3 h-3" />
                      Ver más
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </div>

        <button
          onClick={onDelete}
          className="ml-2 text-red-500 hover:text-red-700"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const MarcasRulesModal: React.FC<MarcasRulesModalProps> = ({
  isOpen,
  onClose,
  clientId,
  marcas
}) => {
  const [rules, setRules] = useState<Rule[]>([]);
  const [newRuleText, setNewRuleText] = useState('');
  const [showNewRuleInput, setShowNewRuleInput] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showMentionSuggestions, setShowMentionSuggestions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Cargar reglas desde Firestore
  useEffect(() => {
    if (!isOpen) return;

    const loadRules = async () => {
      setLoading(true);
      try {
        const docRef = doc(db, 'catalogado', clientId, 'marcas', 'rules');
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          setRules(data.rules || []);
        }
      } catch (error) {
        console.error('Error loading marca rules:', error);
      } finally {
        setLoading(false);
      }
    };

    loadRules();
  }, [isOpen, clientId]);

  // Guardar reglas en Firestore
  const saveRules = async () => {
    setSaving(true);
    try {
      const docRef = doc(db, 'catalogado', clientId, 'marcas', 'rules');
      await setDoc(docRef, {
        rules,
        updatedAt: Timestamp.now()
      });
      
      onClose();
    } catch (error) {
      console.error('Error saving marca rules:', error);
      alert('Error al guardar las reglas');
    } finally {
      setSaving(false);
    }
  };

  // Agregar nueva regla
  const addRule = () => {
    if (!newRuleText.trim()) return;

    const newRule: Rule = {
      id: `rule-${Date.now()}`,
      text: newRuleText.trim(),
      order: rules.length,
      createdAt: Timestamp.now()
    };

    setRules([...rules, newRule]);
    setNewRuleText('');
    setShowNewRuleInput(false);
  };

  // Actualizar regla
  const updateRule = (id: string, text: string) => {
    setRules(rules.map(rule => 
      rule.id === id ? { ...rule, text } : rule
    ));
  };

  // Eliminar regla
  const deleteRule = (id: string) => {
    if (window.confirm('¿Eliminar esta regla?')) {
      setRules(rules.filter(rule => rule.id !== id));
    }
  };

  // Toggle expandir regla
  const toggleRuleExpand = (id: string) => {
    setRules(rules.map(rule => 
      rule.id === id ? { ...rule, expanded: !rule.expanded } : rule
    ));
  };

  // Manejar drag & drop
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = rules.findIndex(r => r.id === active.id);
      const newIndex = rules.findIndex(r => r.id === over.id);
      
      const newRules = [...rules];
      const [movedRule] = newRules.splice(oldIndex, 1);
      newRules.splice(newIndex, 0, movedRule);
      
      // Actualizar orden
      newRules.forEach((rule, index) => {
        rule.order = index;
      });
      
      setRules(newRules);
    }
  };

  // Manejar input con @menciones
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    setNewRuleText(text);
    
    // Detectar si se está escribiendo una @mención
    const cursorPos = e.target.selectionStart;
    setCursorPosition(cursorPos);
    
    const textBeforeCursor = text.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1 && lastAtIndex === textBeforeCursor.length - 1) {
      setShowMentionSuggestions(true);
      setMentionSearch('');
    } else if (lastAtIndex !== -1 && textBeforeCursor.substring(lastAtIndex).match(/^@\w*$/)) {
      const search = textBeforeCursor.substring(lastAtIndex + 1);
      setMentionSearch(search);
      setShowMentionSuggestions(true);
    } else {
      setShowMentionSuggestions(false);
    }
  };

  // Insertar mención
  const insertMention = (marcaName: string) => {
    const textBeforeCursor = newRuleText.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    const textAfterCursor = newRuleText.substring(cursorPosition);
    
    const newText = 
      newRuleText.substring(0, lastAtIndex) + 
      `@${marcaName} ` + 
      textAfterCursor;
    
    setNewRuleText(newText);
    setShowMentionSuggestions(false);
    
    // Focus back to input
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        const newCursorPos = lastAtIndex + marcaName.length + 2;
        inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  const filteredMarcas = marcas.filter(marca =>
    marca.name.toLowerCase().includes(mentionSearch.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        <div className="inline-block w-full max-w-4xl px-4 pt-5 pb-4 overflow-visible text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                Reglas de Catalogación - Marcas
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Reglas generales para todas las marcas
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {loading ? (
            <div className="py-12 text-center">
              <Loader className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">Cargando reglas...</p>
            </div>
          ) : (
            <>
              {/* Lista de reglas */}
              <div className="space-y-3 max-h-[400px] overflow-y-auto relative">
                {rules.length > 0 ? (
                  <DndContext
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={rules.map(r => r.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {rules.map((rule) => (
                        <SortableRule
                          key={rule.id}
                          rule={rule}
                          onUpdate={(text) => updateRule(rule.id, text)}
                          onDelete={() => deleteRule(rule.id)}
                          onToggleExpand={() => toggleRuleExpand(rule.id)}
                          marcas={marcas}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No hay reglas definidas</p>
                  </div>
                )}
              </div>

              {/* Input para nueva regla */}
              {showNewRuleInput ? (
                <div className="mt-4 relative">
                  <textarea
                    ref={inputRef}
                    value={newRuleText}
                    onChange={handleInputChange}
                    placeholder="Escribe una nueva regla... Usa @NombreMarca para referenciar marcas"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent resize-none"
                    style={{
                      '--tw-ring-color': 'rgb(219, 39, 119)'
                    } as React.CSSProperties}
                    rows={3}
                  />
                  
                  {/* Sugerencias de @menciones */}
                  {showMentionSuggestions && filteredMarcas.length > 0 && (
                    <div 
                      className="absolute z-[9999] w-64 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl"
                      style={{ 
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        maxHeight: '200px',
                        overflowY: 'auto'
                      }}
                    >
                      <div className="py-1">
                        {filteredMarcas.map(marca => (
                          <button
                            key={marca.id}
                            onClick={() => insertMention(marca.name)}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center"
                          >
                            <span className="font-medium">@{marca.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex justify-end gap-2 mt-2">
                    <button
                      onClick={() => {
                        setShowNewRuleInput(false);
                        setNewRuleText('');
                      }}
                      className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={addRule}
                      className="px-3 py-1.5 text-sm text-white rounded hover:opacity-90"
                      style={{ backgroundColor: 'rgb(219, 39, 119)' }}
                      disabled={!newRuleText.trim()}
                    >
                      Agregar Regla
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowNewRuleInput(true)}
                  className="mt-4 flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50"
                  style={{ 
                    color: 'rgb(219, 39, 119)',
                    borderColor: 'rgba(219, 39, 119, 0.3)'
                  }}
                >
                  <Plus className="w-4 h-4" />
                  Nueva Regla
                </button>
              )}

              {/* Footer */}
              <div className="mt-6 flex justify-between items-center">
                <div className="text-sm text-gray-500">
                  {rules.length} {rules.length === 1 ? 'regla' : 'reglas'}
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                    disabled={saving}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={saveRules}
                    className="flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:opacity-90"
                    style={{ backgroundColor: 'rgb(219, 39, 119)' }}
                    disabled={saving}
                  >
                    {saving ? (
                      <Loader className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    Guardar Reglas
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarcasRulesModal;